import { useEffect, useMemo, useState } from 'react';
import { useAppStore } from '../../../../app/hooks/useAppStore';
import { isStrictMode } from '../../../../utils/strictMode';
import { runPipelineBatch, computeLiveSystemStats, computeCalibrationStats } from '../../../../governance';
import type { PipelineBatchResult } from '../../../../governance';
import { ALL_QUESTIONS_CORE, QUESTIONS_BY_FAMILY, type Question, TAXONOMY } from '../../../../content';
import { dataLayer } from '../../../../data/dataLayer';
import { isLearnerAccount, isTeacherAccount } from '../../../../accounts';
import { VISUAL_REGISTRY } from '../../../../visuals';
export function useDashboardData() {
  const {
    accounts,
    getLearnerData
  } = useAppStore();
  const [pipelineResult, setPipelineResult] = useState<PipelineBatchResult | null>(null);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [strictModeOn] = useState(() => isStrictMode());

  // Auto-validate ALL questions on mount
  useEffect(() => {
    setPipelineLoading(true);
    const timer = setTimeout(() => {
      try {
        const result = runPipelineBatch(ALL_QUESTIONS_CORE);
        setPipelineResult(result);
      } catch {

        // silent
      } finally {
        setPipelineLoading(false);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  const handleRefreshPipeline = () => {
    setPipelineLoading(true);
    setPipelineResult(null);
    setTimeout(() => {
      try {
        const result = runPipelineBatch(ALL_QUESTIONS_CORE);
        setPipelineResult(result);
      } catch {

        // silent
      } finally {
        setPipelineLoading(false);
      }
    }, 50);
  };

  // Platform stats
  const stats = useMemo(() => {
    const systemStats = computeLiveSystemStats();
    const allAccounts = accounts?.accountsById ? Object.values(accounts.accountsById) : [];
    const learners = allAccounts.filter(isLearnerAccount);
    const teachers = allAccounts.filter(a => a.role === 'teacher');
    const parents = allAccounts.filter(a => a.role === 'parent');
    const schools = new Set(allAccounts.filter(isTeacherAccount).map(a => a.schoolName).filter(Boolean));
    const now = Date.now();
    const todayThreshold = now - 24 * 60 * 60 * 1000;
    const weekThreshold = now - 7 * 24 * 60 * 60 * 1000;
    const activeToday = learners.filter(l => l.lastActive && l.lastActive > todayThreshold).length;
    const activeThisWeek = learners.filter(l => l.lastActive && l.lastActive > weekThreshold).length;
    const freeLearners = learners.filter(l => !l.isPlusUser).length;
    const plusLearners = learners.filter(l => l.isPlusUser).length;
    const total = learners.length || 1;
    const planDistribution = [{
      label: 'Gratis',
      count: freeLearners,
      pct: Math.round(freeLearners / total * 100),
      color: 'bg-gray-400'
    }, {
      label: 'Plus',
      count: plusLearners,
      pct: Math.round(plusLearners / total * 100),
      color: 'bg-blue-500'
    }];
    const topLearners = learners.map(l => {
      const data = getLearnerData(l.id);
      return {
        name: l.displayName,
        xp: data?.stats?.xp ?? 0
      };
    }).sort((a, b) => b.xp - a.xp).slice(0, 3);
    return {
      totalLearners: learners.length,
      totalTeachers: teachers.length,
      totalParents: parents.length,
      totalSchools: schools.size,
      totalQuestions: systemStats.totalQuestions || ALL_QUESTIONS_CORE.length,
      activeToday,
      activeThisWeek,
      planDistribution,
      topLearners
    };
  }, [accounts, getLearnerData]);

  // Content stats
  const contentStats = useMemo(() => {
    const totalFamilies = QUESTIONS_BY_FAMILY.size;
    const taxonomyFamilies = TAXONOMY.length;
    const coveragePct = taxonomyFamilies > 0 ? Math.round(totalFamilies / taxonomyFamilies * 100) : 0;
    const registeredVisuals = Object.keys(VISUAL_REGISTRY).length;
    const usedVisuals = new Set<string>();
    ALL_QUESTIONS_CORE.forEach(q => {
      const aids = [...(q.stimulusAids || []), ...(q.supportAids || [])];
      aids.forEach(aid => {
        if (aid.type) usedVisuals.add(aid.type);
      });
    });
    const visualCoveragePct = registeredVisuals > 0 ? Math.min(100, Math.round(usedVisuals.size / registeredVisuals * 100)) : 0;
    const calibration = computeCalibrationStats();
    return {
      totalFamilies,
      taxonomyFamilies,
      coveragePct,
      totalQuestions: ALL_QUESTIONS_CORE.length,
      registeredVisuals,
      usedVisualsCount: usedVisuals.size,
      visualCoveragePct,
      calibration
    };
  }, []);

  // Validation stats
  const passCount = pipelineResult?.verdicts.PASS ?? null;
  const warnCount = pipelineResult?.verdicts.REVIEW_NEEDED ?? null;
  const failCount = pipelineResult?.verdicts.FAIL ?? null;
  const avgScore = pipelineResult?.avgScore ?? null;
  const totalValidated = passCount !== null ? passCount + (warnCount ?? 0) + (failCount ?? 0) : 0;

  // System health
  const systemHealth = useMemo(() => {
    const storageStats = dataLayer.getStorageStats();
    const storageMb = storageStats.usedMb;
    const storagePct = Math.min(100, Math.round(parseFloat(storageMb) / 5 * 100));
    let responseTime = 45;
    try {
      const [navEntry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      if (navEntry) {
        responseTime = Math.round(navEntry.responseEnd - navEntry.requestStart);
      }
    } catch {

      // Performance API may be unavailable
    }
    return {
      storageMb,
      storagePct,
      responseTime: Math.max(10, responseTime)
    };
  }, []);

  // Recent activity
  const recentActivity = useMemo(() => {
    const activities: Array<{
      time: string;
      message: string;
      type: 'info' | 'success' | 'warning';
    }> = [];
    const now = new Date();
    const formatTime = (offset: number) => new Date(now.getTime() - offset).toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit'
    });
    if (stats.totalLearners > 0) {
      activities.push({
        time: formatTime(0),
        message: `${stats.totalLearners} leerlingen actief`,
        type: 'info'
      });
    }
    if (pipelineResult) {
      activities.push({
        time: formatTime(60000),
        message: `Validatie: ${passCount} geslaagd, ${failCount} gefaald`,
        type: (failCount ?? 0) > 0 ? 'warning' : 'success'
      });
    }
    activities.push({
      time: formatTime(3600000),
      message: `${stats.totalSchools} scholen aangesloten`,
      type: 'info'
    });
    activities.push({
      time: formatTime(7200000),
      message: `${stats.totalQuestions} vragen geladen`,
      type: 'success'
    });
    return activities.slice(0, 4);
  }, [stats, pipelineResult, passCount, failCount]);
  return {
    stats,
    contentStats,
    pipelineResult,
    pipelineLoading,
    strictModeOn,
    handleRefreshPipeline,
    passCount,
    warnCount,
    failCount,
    avgScore,
    totalValidated,
    systemHealth,
    recentActivity
  };
}